import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Check, Bell, Loader2 } from 'lucide-react';
import { MealScheduleSettings } from '@/components/MealScheduleSettings';
import { toast } from 'sonner';
import axios from 'axios';
import logger from '@/utils/logger';

import { GoalStep } from '@/components/onboarding/GoalStep';
import { WeightRateStep } from '@/components/onboarding/WeightRateStep';
import { ProfileStep } from '@/components/onboarding/ProfileStep';
import { ActivityStep, ACTIVITY_LEVELS } from '@/components/onboarding/ActivityStep';
import { CoachingModeStep } from '@/components/onboarding/CoachingModeStep';
import { BehavioralStep } from '@/components/onboarding/BehavioralStep';
import { MacroTargetsStep, ConsentStep } from '@/components/onboarding/MacroConsentSteps';
import { WelcomeStep, HealthModeStep, DietaryStep, FoodDislikesStep, QuickDemoStep } from '@/components/onboarding/NewSteps';

const API = '/api';

export default function Onboarding() {
  const navigate = useNavigate();
  const { token, updateUser, authHeaders } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    goal: '',
    weight_rate: '',
    unit_preference: 'imperial',
    height_cm: '',
    weight_kg: '',
    height_ft: '',
    height_in: '',
    weight_lbs: '',
    age: '',
    biological_sex: '',
    activity_level: '',
    coaching_mode: '',
    biggest_challenge: '',
    motivation_style: '',
    food_relationship: '',
    movement_reminders: null,
    calories_target: 2000,
    protein_target: 150,
    carbs_target: 250,
    fat_target: 65,
    tdee: 0,
    glp1_enabled: false,
    health_modes: [],
    dietary_restrictions: [],
    disliked_foods: [],
  });

  const lbsToKg = (lbs) => Math.round(lbs / 2.20462 * 10) / 10;
  const ftInToCm = (ft, inches) => Math.round((ft * 12 + inches) * 2.54 * 10) / 10;

  const calculateTargets = (data) => {
    let { weight_kg, height_cm, age, biological_sex, activity_level, goal, unit_preference, weight_lbs, height_ft, height_in, weight_rate, glp1_enabled } = data;
    if (unit_preference === 'imperial') {
      if (weight_lbs) weight_kg = lbsToKg(parseFloat(weight_lbs));
      if (height_ft) height_cm = ftInToCm(parseInt(height_ft) || 0, parseInt(height_in) || 0);
    }
    if (!weight_kg || !height_cm || !age || !biological_sex || !activity_level) return data;

    const w = parseFloat(weight_kg);
    const h = parseFloat(height_cm);
    const a = parseInt(age);
    const bmr = biological_sex === 'male'
      ? 10 * w + 6.25 * h - 5 * a + 5
      : 10 * w + 6.25 * h - 5 * a - 161;

    const level = ACTIVITY_LEVELS.find(l => l.id === activity_level);
    const tdee = Math.round(bmr * (level?.multiplier || 1.55));

    const deficitMap = { 'lose_0.5': 250, 'lose_1': 500, 'lose_1.5': 750, 'lose_2': 1000, 'gain_lean': -250, 'gain_standard': -500 };
    const adjustment = deficitMap[weight_rate] || (goal === 'lose_weight' ? 500 : goal === 'gain_muscle' ? -300 : 0);
    let calories = tdee - adjustment;

    const minCal = biological_sex === 'female' ? 1200 : 1500;
    if (calories < minCal) calories = minCal;

    const wLb = w * 2.205;
    let protein;
    if (goal === 'lose_weight') protein = Math.round(wLb * 0.9);
    else if (goal === 'gain_muscle') protein = Math.round(wLb * 1.1);
    else if (glp1_enabled) protein = Math.round(wLb * 1.0);
    else protein = Math.round(wLb * 0.75);

    const fatPct = goal === 'lose_weight' ? 0.27 : 0.25;
    const fat = Math.round((calories * fatPct) / 9);
    const carbs = Math.max(Math.round((calories - protein * 4 - fat * 9) / 4), 50);

    return { ...data, calories_target: calories, protein_target: protein, carbs_target: carbs, fat_target: fat, tdee };
  };

  const MovementStep = () => (
    <div className="space-y-4 max-w-md mx-auto">
      {[
        { val: true, testId: 'movement-yes', label: 'Yes, remind me', desc: "I'll get a daily reminder to stretch, walk, or move a little.", color: '#6B9E8A' },
        { val: false, testId: 'movement-no', label: 'Not right now', desc: 'I can always enable this later in Settings.', color: '#7A7A72' },
      ].map(opt => (
        <button
          key={opt.testId}
          data-testid={opt.testId}
          onClick={() => setFormData(p => ({ ...p, movement_reminders: opt.val }))}
          className={`w-full p-6 rounded-2xl text-left transition-all border-2 flex items-center gap-4 ${
            formData.movement_reminders === opt.val
              ? 'border-[#1A3A4A] dark:border-[#6B9E8A] bg-[#1A3A4A]/5 dark:bg-[#6B9E8A]/10'
              : 'border-transparent bg-white/80 dark:bg-[#141C22]/80 hover:border-[#1A3A4A]/20'
          } shadow-sm`}
        >
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${opt.color}15` }}>
            <Bell className="w-6 h-6" style={{ color: opt.color }} />
          </div>
          <div>
            <h3 className="font-semibold text-[#1A3A4A] dark:text-[#F0EDE8]">{opt.label}</h3>
            <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] mt-1">{opt.desc}</p>
          </div>
        </button>
      ))}
    </div>
  );

  const steps = [
    { title: "", subtitle: "", content: <WelcomeStep />, valid: true },
    { title: "What's your goal?", subtitle: "This helps us personalize your experience", content: <GoalStep formData={formData} setFormData={setFormData} />, valid: formData.goal !== '' },
    ...(formData.goal === 'lose_weight' || formData.goal === 'gain_muscle' ? [{
      title: formData.goal === 'lose_weight' ? "How fast do you want to lose?" : "What kind of muscle gain?",
      subtitle: formData.goal === 'lose_weight' ? "A moderate rate is more sustainable and preserves muscle" : "Lean bulk minimizes fat gain alongside muscle",
      content: <WeightRateStep formData={formData} setFormData={setFormData} />,
      valid: formData.weight_rate !== '',
    }] : []),
    { title: "Do any of these apply to you?", subtitle: "Select all that apply, or skip if none", content: <HealthModeStep formData={formData} setFormData={setFormData} />, valid: true },
    { title: "How should Nuri work for you?", subtitle: "You can change this anytime in Settings", content: <CoachingModeStep formData={formData} setFormData={setFormData} />, valid: formData.coaching_mode !== '' },
    { title: "Any dietary needs?", subtitle: "Select all that apply", content: <DietaryStep formData={formData} setFormData={setFormData} />, valid: true },
    { title: "Any foods you don't like?", subtitle: "Nuri will avoid these in suggestions", content: <FoodDislikesStep formData={formData} setFormData={setFormData} />, valid: true },
    { title: "Here's what Nuri can do", subtitle: "A quick preview of your new food companion", content: <QuickDemoStep />, valid: true },
    { title: "About you", subtitle: "We need a few details to calculate your targets accurately", content: <ProfileStep formData={formData} setFormData={setFormData} />, valid: formData.unit_preference === 'imperial' ? (formData.height_ft && formData.weight_lbs && formData.age && formData.biological_sex) : (formData.height_cm && formData.weight_kg && formData.age && formData.biological_sex) },
    { title: "Activity level", subtitle: "How active are you on a typical week?", content: <ActivityStep formData={formData} setFormData={setFormData} calculateTargets={calculateTargets} />, valid: formData.activity_level !== '' },
    { title: "Help Nuri understand you better", subtitle: "Quick taps \u2014 no typing required", content: <BehavioralStep formData={formData} setFormData={setFormData} />, valid: formData.biggest_challenge && formData.motivation_style && formData.food_relationship },
    { title: "Would you like gentle movement reminders?", subtitle: "A friendly nudge to get moving during your day", content: <MovementStep />, valid: formData.movement_reminders !== null },
    { title: "When do you eat?", subtitle: "We'll send helpful reminders with personalized meal suggestions.", content: <div className="max-w-sm mx-auto"><MealScheduleSettings mode="onboarding" onComplete={() => setStep(s => s + 1)} /></div>, valid: true, skipNext: true },
    { title: "Your daily targets", subtitle: "Personalized for you and your goal", content: <MacroTargetsStep formData={formData} setFormData={setFormData} />, valid: true },
    { title: "Almost there!", subtitle: "One more thing before we start", content: <ConsentStep formData={formData} />, valid: true },
  ];

  const handleNext = async () => {
    const current = steps[step];
    if (current.skipNext) return;
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      try {
        let height_cm, weight_kg;
        if (formData.unit_preference === 'imperial') {
          height_cm = ftInToCm(parseInt(formData.height_ft) || 0, parseInt(formData.height_in) || 0);
          weight_kg = lbsToKg(parseFloat(formData.weight_lbs) || 0);
        } else {
          height_cm = parseFloat(formData.height_cm);
          weight_kg = parseFloat(formData.weight_kg);
        }

        const activityMap = { sedentary: 'sedentary', light: 'lightly_active', moderate: 'moderately_active', very_active: 'very_active', extreme: 'extremely_active' };
        await axios.post(`${API}/user/calculate-tdee`, {
          weight_kg, height_cm,
          age: parseInt(formData.age),
          sex: formData.biological_sex,
          activity_level: activityMap[formData.activity_level] || formData.activity_level,
          goal_type: formData.goal,
          weight_rate: formData.weight_rate || '',
          glp1_enabled: formData.glp1_enabled || false,
        }, authHeaders());

        await axios.put(`${API}/user/profile`, {
          goal: formData.goal,
          goal_type: formData.goal,
          weight_rate: formData.weight_rate || '',
          height_cm, weight_kg,
          age: parseInt(formData.age),
          biological_sex: formData.biological_sex,
          activity_level: formData.activity_level,
          coaching_mode: formData.coaching_mode,
          movement_reminders: formData.movement_reminders === true,
          glp1_enabled: formData.glp1_enabled || false,
          unit_preference: formData.unit_preference,
          biggest_challenge: formData.biggest_challenge,
          motivation_style: formData.motivation_style,
          food_relationship: formData.food_relationship,
          health_modes: formData.health_modes || [],
          dietary_restrictions: formData.dietary_restrictions || [],
          disliked_foods: formData.disliked_foods || [],
          experience_mode: formData.food_relationship === 'healthier_relationship' ? 'gentle'
            : formData.food_relationship === 'love_food' ? 'performance' : 'progress',
          onboarding_complete: true,
        }, authHeaders());

        updateUser({
          onboarding_complete: true,
          experience_mode: formData.food_relationship === 'healthier_relationship' ? 'gentle'
            : formData.food_relationship === 'love_food' ? 'performance' : 'progress',
        });
        navigate('/dashboard', { replace: true });
      } catch (err) {
        logger.error('Onboarding error:', err);
        const msg = err?.response?.data?.detail || err?.message || 'Something went wrong';
        toast.error(`Could not complete setup: ${msg}`);
        setSubmitting(false);
      }
    }
  };

  const current = steps[step];

  return (
    <div data-testid="onboarding-page" style={{ minHeight: '100vh', background: '#FDFBF7', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '24px 24px 0' }}>
        <div style={{ display: 'flex', gap: 6, maxWidth: 420, margin: '0 auto' }}>
          {steps.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= step ? '#C67A5C' : '#E8E3DB',
              transition: 'background 0.4s',
            }} />
          ))}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '32px 20px' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', width: '100%', flex: 1 }}>
          <div style={{
            borderRadius: 24, background: '#FFFFFF', border: '1px solid #E8E3DB',
            padding: '32px 24px', boxShadow: '0 2px 20px rgba(0,0,0,0.04)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }} key={step}>
              <h1 style={{
                fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 400,
                color: '#1A1A18', margin: '0 0 8px', lineHeight: 1.3,
              }}>{current.title}</h1>
              <p style={{
                fontSize: 14, color: '#9A9590', margin: 0,
                fontFamily: 'var(--font-sans)', fontWeight: 500,
              }}>{current.subtitle}</p>
            </div>
            <div key={`content-${step}`}>
              {current.content}
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 420, margin: '24px auto 0', width: '100%', display: 'flex', gap: 12 }}>
          {step > 0 && (
            <Button
              data-testid="onboarding-back"
              onClick={() => setStep(step - 1)}
              variant="ghost"
              style={{
                height: 52, padding: '0 20px', borderRadius: 28,
                color: '#6A6560', background: '#F5F0E8', border: 'none',
                fontWeight: 600, fontFamily: 'var(--font-sans)',
              }}
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
          )}
          <Button
            data-testid="onboarding-next"
            onClick={handleNext}
            disabled={!current.valid || submitting}
            style={{
              flex: 1, height: 52, borderRadius: 28,
              background: current.valid && !submitting ? '#1A1A18' : '#E8E3DB',
              color: current.valid && !submitting ? '#FDFBF7' : '#9A9590',
              border: 'none', fontWeight: 600, fontSize: 15,
              fontFamily: 'var(--font-sans)', cursor: current.valid ? 'pointer' : 'default',
            }}
          >
            {step === steps.length - 1 ? (
              submitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Setting up...</>
              ) : (
                <><Check className="w-5 h-5 mr-2" /> Start Tracking</>
              )
            ) : (
              <>
                {step === steps.indexOf(steps.find(s => s.title === "Your daily targets"))
                  ? "Looks good \u2014 let's go!"
                  : 'Continue'
                }
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
